'use client'

import type { CSSProperties, MouseEventHandler, ReactNode } from 'react'

import Button from 'react-bootstrap/Button'

import Link from '@/components/Link'
import { handleOpenPdf } from '@/utils'

interface PrimaryButtonProps {
  text: ReactNode
  /** Image src rendered before the label. */
  icon?: string
  id?: string
  onClick?: MouseEventHandler<HTMLButtonElement>
  disabled?: boolean
  style?: CSSProperties
  classname?: string
  href?: string
  /** A PDF URL to open on click (lets a Server Component pass a string, not a handler). */
  pdf?: string
  ariaLabel?: string
  /** Visually-hidden descriptive text appended for the SEO/a11y link-text audit. */
  srText?: string
}

function PrimaryButton({
  text,
  icon,
  id,
  onClick,
  disabled,
  style,
  classname,
  href,
  pdf,
  ariaLabel,
  srText
}: PrimaryButtonProps) {
  // With a `pdf`/URL, open it on click from inside this (already client) button —
  // so the parent can pass a URL string instead of an onClick function and
  // stay a Server Component.
  const handleClick = pdf ? () => handleOpenPdf(pdf) : onClick
  const commonProps = {
    variant: 'primary' as const,
    id,
    onClick: handleClick,
    disabled,
    className: classname,
    // Generic labels ("Learn more", "View all") fail the SEO/a11y
    // descriptive-link check; callers pass context via ariaLabel.
    'aria-label': ariaLabel,
    style: {
      ...style,
      background: disabled ? 'grey' : undefined,
      borderWidth: disabled ? 0 : undefined
    }
  }

  const content = (
    <>
      {icon && <img src={icon} width="16" className="pe-1" alt={`${icon}`} />}
      {text}
      {/* Lighthouse's SEO link-text audit reads innerText (not aria-label), so
          generic CTAs ("Learn more") need descriptive text in the DOM. The
          clip-based .visually-hidden keeps it in innerText but off-screen. */}
      {srText ? <span className="visually-hidden"> {srText}</span> : null}
    </>
  )

  return (
    <>
      <style type="text/css">
        {`
          .btn-primary {
            background: linear-gradient(45deg, #070097 0%, #211AFB 100%);
            color: white;
            border-radius: 6px;
            font-weight: 600;

          }
          .btn-primary:hover {
            transition: 0.5s;
          }
        `}
      </style>

      {/* With an href, render as a prefetching Next Link styled as the button. */}
      <Button
        {...commonProps}
        // react-bootstrap's polymorphic `as` is unsatisfiable for components under
        // @types/react 19 (it resolves to `As & keyof IntrinsicElements`). The
        // runtime render (an <a> with the btn classes) is correct.
        // @ts-expect-error -- known react-bootstrap × React 19 typings gap
        as={href ? Link : undefined}
        href={href}
      >
        {content}
      </Button>
    </>
  )
}

export default PrimaryButton
